import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
  UpdateMembershipVariantDto,
  MembershipVariantDto,
  UserMembershipTypeDto,
} from '../../../models/user-membership/dto/user-membership.dto';
import { GET_USER_MEMBERSHIP_TYPES_QUERY_KEY } from './useGetUserMembershipTypes';

/**
 * PUT user-membership-types/:typeId/variants/:variantId. Same tenancy shape
 * as useCreateMembershipVariant — org is derived server-side from the loaded
 * type, `organizationId` here only locates the cached list to patch.
 */
export const useUpdateMembershipVariant = (
  organizationId: string,
  membershipTypeId: string,
) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateMembershipVariant', membershipTypeId],
    mutationFn: ({
      variantId,
      body,
    }: {
      variantId: string;
      body: UpdateMembershipVariantDto;
    }) =>
      apiClient.fetchApi<MembershipVariantDto, UpdateMembershipVariantDto>({
        method: 'PUT',
        endpoint: `user-membership-types/${membershipTypeId}/variants/${variantId}`,
        body,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<UserMembershipTypeDto[]>(
        GET_USER_MEMBERSHIP_TYPES_QUERY_KEY(organizationId),
        (previous) => {
          if (!previous) return previous;
          return previous.map((type) => {
            if (type.id !== membershipTypeId) return type;
            const existingVariants = type.variants ?? [];
            // Same "at most one live default" demotion as create — promoting
            // this variant to default locally demotes whichever other one
            // held it, mirroring promoteAsDefaultVariant server-side.
            const nextVariants = existingVariants.map((v) => {
              if (v.id === updated.id) return updated;
              return updated.isDefault ? { ...v, isDefault: false } : v;
            });
            return { ...type, variants: nextVariants };
          });
        },
      );
    },
  });
};
